// Administración: alias de proveedores, empresas, tarjetas y usuarios.
//
// POR QUÉ ESTA PANTALLA ES IMPORTANTE
// Es el 90% de lo que el autor original va a querer cambiar. En la app vieja
// esas cosas estaban en el código (ALIAS_PROV, CATALOGO_MAESTRO) y cambiarlas
// exigía editar el HTML. Acá son datos: los edita desde el sistema, sin pasar
// por GitHub y sin esperar a nadie.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, descargar } from '../api';
import { useSesion } from '../sesion';
import type { Alias, Empresa, Usuario } from '../tipos';
import { CambiarClave } from '../componentes/CambiarClave';

type Panel = 'alias' | 'tarjetas' | 'usuarios' | 'auditoria' | 'clave';

export function Administracion() {
  const { puede, soloLectura } = useSesion();
  const [panel, setPanel] = useState<Panel>('alias');

  const PANELES: { id: Panel; titulo: string; soloSupervisor?: boolean }[] = [
    { id: 'alias', titulo: 'Alias de proveedores' },
    { id: 'tarjetas', titulo: 'Empresas y tarjetas' },
    { id: 'usuarios', titulo: 'Usuarios', soloSupervisor: true },
    { id: 'auditoria', titulo: 'Cierre de ejercicio' },
    { id: 'clave', titulo: 'Mi contraseña' },
  ];

  return (
    <>
      <div className="tabs" style={{ marginTop: 4 }}>
        {PANELES.filter((p) => !p.soloSupervisor || puede('supervisor')).map((p) => (
          <button key={p.id} className={`tab ${panel === p.id ? 'activa' : ''}`}
                  onClick={() => setPanel(p.id)}>
            {p.titulo}
          </button>
        ))}
      </div>

      {soloLectura && <div className="aviso info">Tu rol es <b>auditor</b>: solo lectura.</div>}

      {panel === 'alias' && <PanelAlias />}
      {panel === 'tarjetas' && <PanelTarjetas />}
      {panel === 'usuarios' && <PanelUsuarios />}
      {panel === 'auditoria' && <PanelAuditoria />}
      {panel === 'clave' && <CambiarClave />}
    </>
  );
}

/* ------------------------------------------------------------------- alias */
function PanelAlias() {
  const { soloLectura } = useSesion();
  const qc = useQueryClient();
  const [patron, setPatron] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [error, setError] = useState('');

  const q = useQuery({ queryKey: ['alias'], queryFn: () => api.get<Alias[]>('/api/alias') });

  const crear = useMutation({
    mutationFn: () => api.post('/api/alias', { patron, proveedor, orden: (q.data?.length ?? 0) }),
    onSuccess: () => { setPatron(''); setProveedor(''); setError(''); void qc.invalidateQueries({ queryKey: ['alias'] }); },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo agregar.'),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => api.borrar(`/api/alias/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['alias'] }),
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo borrar.'),
  });

  return (
    <>
      <h2 className="seccion">
        <span className="num">A</span>ALIAS DE PROVEEDORES
        <span className="muted">— cuando el resumen muestra un nombre y la contabilidad otro</span>
      </h2>
      <div className="kb">
        <div className="pad">
          <p className="muted" style={{ marginTop: 0 }}>
            Si el resumen del emisor trae <b>AUTOPISTAS URBANAS</b> y en el mayor figura como{' '}
            <b>AUSA</b>, se da de alta acá y la app lo reconoce solo en los próximos resúmenes.
            El <b>orden</b> importa: se usa el primer patrón que coincide.
          </p>
          {!soloLectura && (
            <div className="controls" style={{ margin: 0, border: 'none', padding: 0, background: 'none' }}>
              <div>
                <label htmlFor="pat">Texto que aparece en el resumen</label>
                <input id="pat" type="text" value={patron} placeholder="AUTOPISTAS URBANAS"
                       onChange={(e) => setPatron(e.target.value)} />
              </div>
              <div>
                <label htmlFor="prov">Proveedor en la contabilidad</label>
                <input id="prov" type="text" value={proveedor} placeholder="AUSA"
                       onChange={(e) => setProveedor(e.target.value)} />
              </div>
              <button className="btn-run" disabled={patron.length < 2 || !proveedor || crear.isPending}
                      onClick={() => crear.mutate()}>
                Agregar alias
              </button>
            </div>
          )}
          {error && <div className="aviso bad" role="alert">{error}</div>}
        </div>
      </div>

      <div className="scroll" style={{ marginTop: 10 }}>
        <table>
          <thead>
            <tr><th className="num">Orden</th><th>Texto en el resumen</th><th>Proveedor</th><th></th></tr>
          </thead>
          <tbody>
            {q.data?.map((a) => (
              <tr key={a.id}>
                <td className="num">{a.orden}</td>
                <td>{a.patron}</td>
                <td><b>{a.proveedor}</b></td>
                <td>
                  {!soloLectura && (
                    <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                            onClick={() => { if (confirm(`¿Quitar el alias "${a.patron}"?`)) borrar.mutate(a.id); }}>
                      quitar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- tarjetas */
function PanelTarjetas() {
  const { puede, soloLectura } = useSesion();
  const qc = useQueryClient();
  const [empresaId, setEmpresaId] = useState('');
  const [nombre, setNombre] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [emisor, setEmisor] = useState('credicoop');
  const [error, setError] = useState('');

  const q = useQuery({ queryKey: ['empresas'], queryFn: () => api.get<Empresa[]>('/api/empresas') });

  const crear = useMutation({
    mutationFn: () => api.post('/api/tarjetas', { empresaId, nombre, cuentaContable: cuenta, emisor }),
    onSuccess: () => { setNombre(''); setCuenta(''); setError(''); void qc.invalidateQueries({ queryKey: ['empresas'] }); },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo agregar.'),
  });

  return (
    <>
      <h2 className="seccion">
        <span className="num">B</span>EMPRESAS Y TARJETAS
        <span className="muted">— con su cuenta contable de pasivo</span>
      </h2>

      {puede('supervisor') && !soloLectura && (
        <div className="kb">
          <div className="pad">
            <div className="controls" style={{ margin: 0, border: 'none', padding: 0, background: 'none' }}>
              <div>
                <label htmlFor="e">Empresa</label>
                <select id="e" value={empresaId} onChange={(ev) => setEmpresaId(ev.target.value)}>
                  <option value="">— elegí —</option>
                  {q.data?.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="n">Nombre de la tarjeta</label>
                <input id="n" type="text" value={nombre} placeholder="Visa Banco Credicoop"
                       onChange={(e) => setNombre(e.target.value)} />
              </div>
              <div>
                <label htmlFor="ct">Cuenta contable</label>
                <input id="ct" type="text" value={cuenta} placeholder="215111"
                       onChange={(e) => setCuenta(e.target.value)} style={{ width: 110 }} />
              </div>
              <div>
                <label htmlFor="em">Emisor</label>
                <select id="em" value={emisor} onChange={(e) => setEmisor(e.target.value)}>
                  <option value="credicoop">Credicoop</option>
                  <option value="galicia">Galicia</option>
                  <option value="nacion">Banco Nación</option>
                  <option value="amex">American Express</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <button className="btn-run" disabled={!empresaId || !nombre || !cuenta || crear.isPending}
                      onClick={() => crear.mutate()}>
                Agregar tarjeta
              </button>
            </div>
            {error && <div className="aviso bad" role="alert">{error}</div>}
          </div>
        </div>
      )}

      <div className="scroll" style={{ marginTop: 10 }}>
        <table>
          <thead><tr><th>Empresa</th><th>Tarjeta</th><th>Cuenta</th><th>Emisor</th></tr></thead>
          <tbody>
            {q.data?.flatMap((e) =>
              e.tarjetas.length
                ? e.tarjetas.map((t) => (
                    <tr key={t.id}>
                      <td>{e.nombre}</td><td>{t.nombre}</td>
                      <td className="cn">{t.cuentaContable}</td>
                      <td className="muted">{t.emisor ?? '—'}</td>
                    </tr>
                  ))
                : [<tr key={e.id}><td>{e.nombre}</td>
                    <td colSpan={3} className="muted">sin tarjetas dadas de alta</td></tr>],
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- usuarios */
function PanelUsuarios() {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const q = useQuery({ queryKey: ['usuarios'], queryFn: () => api.get<(Usuario & { activo: boolean })[]>('/api/usuarios') });

  const [aviso, setAviso] = useState('');

  const desactivar = useMutation({
    mutationFn: (id: string) => api.post(`/api/usuarios/${id}/desactivar`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usuarios'] }),
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo desactivar.'),
  });

  // Restablecer la clave de otro: para cuando alguien pierde la suya. Cierra
  // sus sesiones, así que tiene que volver a entrar con la nueva.
  const restablecer = useMutation({
    mutationFn: (v: { id: string; claveNueva: string }) =>
      api.post<{ email: string; sesionesCerradas: number }>(
        `/api/usuarios/${v.id}/clave`, { claveNueva: v.claveNueva }),
    onSuccess: (r) => {
      setError('');
      setAviso(`Contraseña de ${r.email} restablecida. Se cerraron ${r.sesionesCerradas} sesión(es): tiene que entrar con la nueva.`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo restablecer.'),
  });

  const ROL: Record<string, string> = {
    operador: 'concilia y edita', supervisor: 'cierra, firma y administra', auditor: 'solo lectura',
  };

  return (
    <>
      <h2 className="seccion">
        <span className="num">C</span>USUARIOS
        <span className="muted">— quién puede conciliar, quién puede firmar</span>
      </h2>
      {error && <div className="aviso bad" role="alert">{error}</div>}
      {aviso && <div className="aviso ok" role="status">{aviso}</div>}
      <div className="scroll">
        <table>
          <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Estado</th><th></th></tr></thead>
          <tbody>
            {q.data?.map((u) => (
              <tr key={u.id}>
                <td><b>{u.nombre}</b></td>
                <td className="muted">{u.email}</td>
                <td><span className="tag info">{u.rol}</span> <span className="muted">{ROL[u.rol]}</span></td>
                <td><span className={`tag ${u.activo ? 'ok' : 'bad'}`}>{u.activo ? 'activo' : 'desactivado'}</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {u.activo && (
                    <>
                      <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                              onClick={() => {
                                const c = prompt(`Contraseña nueva para ${u.nombre} (al menos 10 caracteres).\n\nSe le cierran las sesiones y tiene que entrar con esta.`);
                                if (c) restablecer.mutate({ id: u.id, claveNueva: c });
                              }}>
                        restablecer clave
                      </button>
                      <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px', marginLeft: 4 }}
                              onClick={() => {
                                if (confirm(`¿Desactivar a ${u.nombre}?\n\nSu sesión se cierra en el acto.`)) desactivar.mutate(u.id);
                              }}>
                        desactivar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* --------------------------------------------------------------- auditoría */
function PanelAuditoria() {
  const [empresaId, setEmpresaId] = useState('');
  const [corte, setCorte] = useState(`${new Date().getFullYear() - 1}-12-31`);
  const [error, setError] = useState('');
  const q = useQuery({ queryKey: ['empresas'], queryFn: () => api.get<Empresa[]>('/api/empresas') });

  return (
    <>
      <h2 className="seccion">
        <span className="num">D</span>COMPOSICIÓN DEL SALDO
        <span className="muted">— el papel de trabajo para el legajo del balance</span>
      </h2>
      <div className="kb">
        <div className="pad">
          <p className="muted" style={{ marginTop: 0 }}>
            Genera el Excel con el saldo contable de cada tarjeta de la empresa a la fecha de
            corte y la conciliación que lo respalda, más las partidas que quedaron pendientes.
          </p>
          <div className="controls" style={{ margin: 0, border: 'none', padding: 0, background: 'none' }}>
            <div>
              <label htmlFor="ae">Empresa</label>
              <select id="ae" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
                <option value="">— elegí —</option>
                {q.data?.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ac">Fecha de corte</label>
              <input id="ac" type="date" value={corte} onChange={(e) => setCorte(e.target.value)} />
            </div>
            <button className="btn-ok" disabled={!empresaId}
                    onClick={() => {
                      setError('');
                      void descargar(`/api/empresas/${empresaId}/auditoria?corte=${corte}`)
                        .catch((e) => setError(e.message));
                    }}>
              Descargar Excel
            </button>
          </div>
          {error && <div className="aviso bad" role="alert">{error}</div>}
        </div>
      </div>
    </>
  );
}
